// components/chat/MessagesContent.jsx
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Search, ArrowLeft, Plus, MessageSquare } from 'lucide-react';
import BackNav from './common/BackNavButton';
import ChatHeader from './chat/ChatHeader';
import AuthService from '../services/auth.service';
import MessageService from '../services/message.service';
import AppointmentService from '../services/appoinment.service';
import ConversationsList from './chat/ConversationsList';
import PropTypes from 'prop-types';
import { parseServerDateTime, formatMessageDateTime } from '../utils/dateUtils';

const MessagesContent = ({
  userRole,
  nameField,
  specialtyField,
  avatarInitial,
  messageColor,
  replyColor,
  useBackNav = false,
  useChatHeader = false
}) => {
  const [conversations, setConversations] = useState([]);
  const [appointmentContacts, setAppointmentContacts] = useState([]);
  const [currentMessages, setCurrentMessages] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('conversations');
  const [messageContent, setMessageContent] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const isDetailView = location.pathname.includes('/messagedetails/');
  const conversationId = isDetailView ? id : null;
  const basePath = `/${userRole}/messages`;
  const detailPath = `/${userRole}/messagedetails/`;

  // Avvolgi le funzioni di caricamento con useCallback per evitare loop infiniti
  const loadConversations = useCallback(() =>
    MessageService.loadConversationsData(setLoading, setConversations),
    []);

  const loadAppointmentContacts = useCallback(() =>
    MessageService.loadAppointmentContactsData(userRole, specialtyField, setLoading, setAppointmentContacts, AppointmentService),
    [userRole, specialtyField]);

  const loadMessages = useCallback((userId) =>
    MessageService.loadMessagesForContact(userId, appointmentContacts, conversations, setLoading, setCurrentMessages, setSelectedConversation),
    [appointmentContacts, conversations]);

  // Load initial data
  useEffect(() => {
    loadConversations();
    loadAppointmentContacts();
  }, [loadConversations, loadAppointmentContacts]);

  // Load messages when conversation ID changes
  useEffect(() => {
    if (conversationId) {
      loadMessages(conversationId);
    }
  }, [conversationId, loadMessages]);

  // Polling per la lista conversazioni (ogni 15 secondi)
  useEffect(() => {
    // Imposta l'intervallo per la lista conversazioni
    const listInterval = setInterval(() => {
      if (!isDetailView) {
        loadConversations();
      }
    }, 15000); // 15 secondi

    return () => clearInterval(listInterval);
  }, [isDetailView, loadConversations]);

  // Polling separato per il dettaglio conversazione (ogni 5 secondi)
  useEffect(() => {
    if (isDetailView && conversationId) {
      // Imposta l'intervallo per il dettaglio
      const detailInterval = setInterval(() => {
        loadMessages(conversationId);
      }, 5000); // 5 secondi

      return () => clearInterval(detailInterval);
    }
  }, [isDetailView, conversationId, loadMessages]);

  // Navigation handlers
  const handleSelectConversation = (id) => navigate(`${detailPath}${id}`);
  const handleBackToList = () => navigate(basePath);

  const handleConversationDeleted = (deletedId) => {
    setConversations(prev => prev.filter(conv => conv.id !== deletedId));
    if (selectedConversation && selectedConversation.id === deletedId) {
      navigate(basePath);
    }
  };

const handleSendMessage = async () => {
  if (!messageContent.trim() || !conversationId) return;
  
  try {
    console.log(' DEBUG: Sending message to user ID:', conversationId);
    
    // 🎯 FIX: Usa direttamente conversationId (che è l'ID utente corretto)
    await MessageService.sendMessage(conversationId, messageContent);
    setMessageContent('');
    
    console.log(' DEBUG: Message sent, refreshing messages...');
    
    // Aggiorna immediatamente i messaggi dopo l'invio
    await loadMessages(conversationId);
    
    // Anche l'elenco delle conversazioni dovrebbe essere aggiornato
    loadConversations();
    
    // Il resto del codice rimane invariato
    const existingConversation = MessageService.findContactById(conversations, conversationId);
    if (!existingConversation) {
      loadConversations();
    }
  } catch (error) {
    console.error('[DEBUG] Error sending message:', error);
  }
};

  // Filter conversations and contacts
  const filteredConversations = MessageService.filterContactsBySearchTerm(conversations, searchTerm);
  const filteredContacts = MessageService.filterContactsBySearchTerm(appointmentContacts, searchTerm);
  const contactsWithoutConversation = MessageService.filterContactsWithoutConversations(
    filteredContacts, conversations
  );

  return (
    <div className="h-full flex flex-col">
      {!isDetailView ? (
        // Vista elenco conversazioni
        <>
          <header className="bg-white p-4 shadow-sm">
            <h1 className="text-2xl font-bold mb-4">
              {userRole === 'doctor' ? 'Messaggi con pazienti' : 'Messaggi con professionisti'}
            </h1>
            <div className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Cerca nelle conversazioni..."
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <Search className="absolute left-3 top-2.5 text-gray-400 w-5 h-5" />
            </div>
          </header>

          {/* Tabs */}
          <div className="flex border-b bg-white">
            <button
              onClick={() => setActiveTab('conversations')}
              className={`flex-1 py-3 font-medium text-center ${activeTab === 'conversations'
                ? 'text-purple-600 border-b-2 border-purple-600'
                : 'text-gray-500'
                }`}
            >
              <MessageSquare className="w-4 h-4 inline-block mr-1" />
              Conversazioni attive
            </button>
            <button
              onClick={() => setActiveTab('contacts')}
              className={`flex-1 py-3 font-medium text-center ${activeTab === 'contacts'
                ? 'text-purple-600 border-b-2 border-purple-600'
                : 'text-gray-500'
                }`}
            >
              <Plus className="w-4 h-4 inline-block mr-1" />
              Nuova conversazione
            </button>
          </div>

          {loading && conversations.length === 0 && contactsWithoutConversation.length === 0 ? (
            <div className="flex justify-center items-center h-64">
              <p>Caricamento...</p>
            </div>
          ) : activeTab === 'conversations' ? (
            // Tab conversazioni attive
            filteredConversations.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-center px-4">
                <MessageSquare className="w-12 h-12 text-gray-300 mb-2" />
                <p className="text-gray-600">Non hai ancora conversazioni attive</p>
                <button
                  onClick={() => setActiveTab('contacts')}
                  className="mt-4 text-purple-600 hover:underline"
                >
                  Inizia una nuova conversazione
                </button>
              </div>
            ) : (
              <ConversationsList
                conversations={filteredConversations}
                onSelectConversation={handleSelectConversation}
                onConversationDeleted={handleConversationDeleted}
                userRole={userRole}
                nameField={nameField}
                specialtyField={specialtyField}
              />
            )
          ) : (
            // Tab nuova conversazione (contatti da appuntamento)
            <ContactListSection
              contacts={contactsWithoutConversation}
              filteredContacts={filteredContacts}
              userRole={userRole}
              avatarInitial={avatarInitial}
              specialtyField={specialtyField}
              handleSelectConversation={handleSelectConversation}
            />
          )}
        </>
      ) : (
        // Vista dettaglio conversazione
        <MessageDetailSection
          loading={loading}
          userRole={userRole}
          useBackNav={useBackNav}
          useChatHeader={useChatHeader}
          selectedConversation={selectedConversation}
          currentMessages={currentMessages}
          handleBackToList={handleBackToList}
          messageContent={messageContent}
          setMessageContent={setMessageContent}
          handleSendMessage={handleSendMessage}
          messageColor={messageColor}
          replyColor={replyColor}
          avatarInitial={avatarInitial}
        />
      )}
    </div>
  );
};

// Componente per la lista dei contatti
const ContactListSection = ({
  contacts,
  filteredContacts,
  userRole,
  avatarInitial,
  specialtyField,
  handleSelectConversation
}) => {
  if (contacts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center px-4">
        <MessageSquare className="w-12 h-12 text-gray-300 mb-2" />
        <p className="text-gray-600">
          {filteredContacts.length === 0
            ? `Non hai appuntamenti con ${userRole === 'patient' ? 'professionisti' : 'pazienti'}`
            : 'Hai già iniziato conversazioni con tutti i tuoi contatti'}
        </p>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto bg-white">
      {contacts.map((contact) => (
        <div
          key={contact.id}
          className="relative flex items-center p-4 border-b hover:bg-gray-50 cursor-pointer"
          onClick={() => handleSelectConversation(contact.id)}
        >
          <div className="w-12 h-12 rounded-full flex items-center justify-center border-2 border-gray-200 overflow-hidden">
            {contact.profile_picture ? (
              <img
                src={`/storage/${contact.profile_picture}`}
                alt={`${contact.first_name} ${contact.last_name}`}
                className="w-full h-full object-cover"
                onError={(e) => {
                  const img = e.target;
                  if (img instanceof HTMLImageElement) {
                    img.onerror = null;
                    if (img.parentNode && img.parentNode instanceof HTMLElement) {
                      img.parentNode.innerHTML = `<div class="w-full h-full bg-purple-100 flex items-center justify-center"><span class="text-purple-600 font-bold">${avatarInitial}</span></div>`;
                    }
                  }
                }}
              />
            ) : (
              <div className="w-full h-full bg-purple-100 flex items-center justify-center">
                <span className="text-purple-600 font-bold">{avatarInitial}</span>
              </div>
            )}
          </div>

          <div className="flex-1 ml-4">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-semibold">{`${contact.first_name} ${contact.last_name}`}</h3>
                <p className="text-sm text-gray-500">{contact[specialtyField]}</p>
              </div>
              <div className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full">
                Nuova chat
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Componente per la visualizzazione dettaglio messaggio
const MessageDetailSection = ({ 
  loading, 
  userRole, 
  useBackNav, 
  useChatHeader, 
  selectedConversation, 
  currentMessages,
  handleBackToList,
  messageContent,
  setMessageContent,
  handleSendMessage,
  messageColor,
  replyColor,
  avatarInitial
}) => {
  return (
      <div 
        className="flex flex-col bg-white"
        style={{ 
          height: 'calc(100vh - 130px)',
          maxHeight: 'calc(100vh - 160px)',
          overflow: 'hidden'
        }}
      >      
      {/*  HEADER FISSO */}
      {useBackNav ? (
        <BackNav />
      ) : (
        <header className="bg-white p-4 shadow-sm flex-shrink-0">
          <button
            onClick={handleBackToList}
            className="flex items-center text-purple-600 mb-4"
          >
            <ArrowLeft className="w-5 h-5 mr-1" />
            Torna alla lista
          </button>
          {selectedConversation && !useChatHeader && (
            <div className="flex items-center">
              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                <span className="text-purple-600 font-bold">
                  {`${selectedConversation.first_name?.charAt(0) || ''}${selectedConversation.last_name?.charAt(0) || ''}`}
                </span>
              </div>
              <div className="ml-3">
                <h2 className="font-semibold">{`${selectedConversation.first_name || ''} ${selectedConversation.last_name || ''}`}</h2>
                <p className="text-sm text-gray-500">
                  {userRole === 'patient'
                    ? selectedConversation.therapist?.specialization
                    : selectedConversation.email}
                </p>
              </div>
            </div>
          )}
        </header>
      )}

      {useChatHeader && selectedConversation && (
        <div className="flex-shrink-0">
          <ChatHeader
            messages={currentMessages}
            currentUserId={AuthService.getCurrentUserId()}
          />
        </div>
      )}

      {/*  AREA MESSAGGI CON SCROLL INTERNO */}
      <div className="flex-1 overflow-hidden">
        {currentMessages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center bg-gray-50">
            <div className="text-center max-w-md px-4">
              <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Inizia una conversazione
              </h3>
              <p className="text-gray-500 mb-6">
                {userRole === 'patient'
                  ? `Scrivi al tuo professionista per chiedere informazioni o supporto.`
                  : `Scrivi al tuo paziente per fornire indicazioni o supporto.`}
              </p>
            </div>
          </div>
        ) : (
          <MessagesList
            messages={currentMessages}
            userRole={userRole}
            messageColor={messageColor}
            replyColor={replyColor}
            avatarInitial={avatarInitial}
          />
        )}
      </div>

      {/*  INPUT MESSAGGIO FISSO IN BASSO */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSendMessage();
        }}
        className="bg-white p-4 border-t flex gap-2 flex-shrink-0"
      >
        <input
          type="text"
          value={messageContent}
          onChange={(e) => setMessageContent(e.target.value)}
          placeholder="Scrivi un messaggio..."
          className="flex-1 p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
        />
        <button
          type="submit"
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
        >
          Invia
        </button>
      </form>
    </div>
  );
};

// Componente per la lista dei messaggi
const MessagesList = ({ messages, userRole, messageColor, replyColor, avatarInitial }) => {
  const messagesEndRef = useRef(null);
  const containerRef = useRef(null);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const [showNewMessagesBadge, setShowNewMessagesBadge] = useState(false);
  const [lastSeenMessageCount, setLastSeenMessageCount] = useState(0);

  // Funzione per controllare se l'utente è vicino al fondo
  const isNearBottom = () => {
    if (!containerRef.current) return true;
    
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    const threshold = 100; // pixel dal fondo
    
    return scrollHeight - scrollTop - clientHeight < threshold;
  };

  // Scroll automatico intelligente
  const scrollToBottom = (force = false) => {
    if (force || (!isUserScrolling && isNearBottom())) {
      messagesEndRef.current?.scrollIntoView({ 
        behavior: 'smooth',
        block: 'end'
      });
      setShowNewMessagesBadge(false);
      setLastSeenMessageCount(messages.length);
    }
  };

  // Gestione scroll dell'utente
  const handleScroll = () => {
    if (!containerRef.current) return;
    
    const isAtBottom = isNearBottom();
    
    if (isAtBottom) {
      // Se torna vicino al fondo, riattiva auto-scroll
      setIsUserScrolling(false);
      setShowNewMessagesBadge(false);
      setLastSeenMessageCount(messages.length);
    } else {
      // Se ha scrollato su, disattiva auto-scroll
      setIsUserScrolling(true);
    }
  };

  // Quando cambiano i messaggi
  useEffect(() => {
    const newMessageCount = messages.length;
    
    if (newMessageCount > lastSeenMessageCount) {
      // Ci sono nuovi messaggi
      if (isUserScrolling && !isNearBottom()) {
        // L'utente sta leggendo sopra - mostra badge invece di scrollare
        setShowNewMessagesBadge(true);
      } else {
        // L'utente è in fondo - scroll automatico
        scrollToBottom();
      }
    }
  }, [messages, lastSeenMessageCount, isUserScrolling]);

  // Scroll immediato al caricamento iniziale della chat
  useEffect(() => {
    if (messages.length > 0 && lastSeenMessageCount === 0) {
      // Prima volta che carica questa chat - vai subito al fondo
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ block: 'end' });
        setLastSeenMessageCount(messages.length);
      }, 100);
    }
  }, [messages.length > 0]);

  // Click sul badge per andare ai nuovi messaggi
  const handleNewMessagesClick = () => {
    scrollToBottom(true); // Force scroll
    setIsUserScrolling(false);
  };

  return (
    <div className="relative h-full">
      {/* Container messaggi con scroll */}
      <div 
        ref={containerRef}
        className="h-full overflow-y-auto p-4 bg-gray-50"
        onScroll={handleScroll}
      >
        {messages.map((message) => {
          const isOtherUser = message.sender_id !== AuthService.getCurrentUserId();

          return (
            <div
              key={message.id}
              className={`mb-4 flex ${isOtherUser ? 'justify-start' : 'justify-end'}`}
            >
              {isOtherUser && (
                <div className={`w-8 h-8 bg-${messageColor}-100 rounded-full flex items-center justify-center mr-2 flex-shrink-0`}>
                  <span className={`text-${messageColor}-600 text-sm font-semibold`}>{avatarInitial}</span>
                </div>
              )}

              <div className="flex flex-col max-w-[70%]">
                <div
                  className={`rounded-lg p-3 ${isOtherUser
                      ? `bg-${messageColor}-500 text-white rounded-bl-none`
                      : `bg-${replyColor}-500 text-white rounded-br-none`
                    }`}
                >
                  <p className="break-words">{message.content}</p>
                </div>
                <div
                  className={`text-xs mt-1 text-gray-500 ${isOtherUser ? 'text-left' : 'text-right'}`}
                >
                  {formatMessageDateTime(message.created_at)}
                </div>
              </div>

              {!isOtherUser && (
                <div className={`w-8 h-8 bg-${replyColor}-100 rounded-full flex items-center justify-center ml-2 flex-shrink-0`}>
                  <span className={`text-${replyColor}-600 text-sm font-semibold`}>
                    {userRole === 'doctor' ? 'D' : 'P'}
                  </span>
                </div>
              )}
            </div>
          );
        })}
        
        {/* Elemento per auto-scroll */}
        <div ref={messagesEndRef} />
      </div>

      {/* Badge "Nuovi messaggi" */}
      {showNewMessagesBadge && (
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-10">
          <button
            onClick={handleNewMessagesClick}
            className="bg-purple-600 text-white px-4 py-2 rounded-full shadow-lg hover:bg-purple-700 transition-colors animate-bounce"
          >
            ↓ Nuovi messaggi ({messages.length - lastSeenMessageCount})
          </button>
        </div>
      )}
    </div>
  );
};


// PropTypes per i componenti interni
ContactListSection.propTypes = {
  contacts: PropTypes.array.isRequired,
  filteredContacts: PropTypes.array.isRequired,
  userRole: PropTypes.string.isRequired,
  avatarInitial: PropTypes.string,
  specialtyField: PropTypes.string.isRequired,
  handleSelectConversation: PropTypes.func.isRequired
};

MessageDetailSection.propTypes = {
  loading: PropTypes.bool.isRequired,
  userRole: PropTypes.string.isRequired,
  useBackNav: PropTypes.bool.isRequired,
  useChatHeader: PropTypes.bool.isRequired,
  selectedConversation: PropTypes.object,
  currentMessages: PropTypes.array.isRequired,
  handleBackToList: PropTypes.func.isRequired,
  messageContent: PropTypes.string.isRequired,
  setMessageContent: PropTypes.func.isRequired,
  handleSendMessage: PropTypes.func.isRequired,
  messageColor: PropTypes.string,
  replyColor: PropTypes.string,
  avatarInitial: PropTypes.string
};

MessagesList.propTypes = {
  messages: PropTypes.array.isRequired,
  userRole: PropTypes.string.isRequired,
  messageColor: PropTypes.string,
  replyColor: PropTypes.string,
  avatarInitial: PropTypes.string
};

// Aggiungi la validazione delle props
MessagesContent.propTypes = {
  userRole: PropTypes.string.isRequired,
  nameField: PropTypes.string.isRequired,
  specialtyField: PropTypes.string.isRequired,
  avatarInitial: PropTypes.string,
  messageColor: PropTypes.string,
  replyColor: PropTypes.string,
  useBackNav: PropTypes.bool,
  useChatHeader: PropTypes.bool
};

// Aggiungi i valori predefiniti
MessagesContent.defaultProps = {
  avatarInitial: null,
  messageColor: null,
  replyColor: null,
  useBackNav: false,
  useChatHeader: false
};

export default MessagesContent;